/*
 * OC Recruitment — For Packaging Companies
 * Design: "The Boardroom" Corporate Modernism — Boutique Retained Search Firm
 * Updated: single Retained Search positioning, headhunting & mapping as methods
 */

import { Link } from "wouter";
import { ArrowRight, Search, Target, Users, Clock, ShieldCheck, BarChart3 } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import FAQ from "@/components/FAQ";
import Breadcrumbs from "@/components/Breadcrumbs";
import { pageSEO, organizationSchema, serviceSchema } from "@/lib/seoConfig";

const companiesFAQs = [
  {
    question: "How much does a retained search for packaging sales talent cost?",
    answer: "Retained search fees are typically structured as a percentage of the placed candidate's first-year compensation. The exact fee depends on the seniority and complexity of the role. We provide a clear fee proposal upfront before any engagement begins, with no hidden costs.",
  },
  {
    question: "What if the candidate you place doesn't work out?",
    answer: "All retained search assignments include a guarantee period. If a placed candidate leaves or is terminated within the agreed timeframe, we will conduct a replacement search at no additional fee. We are invested in long-term success, not just filling a seat.",
  },
  {
    question: "Can you recruit for packaging sales roles outside the United States?",
    answer: "Our primary focus is the US packaging market. However, with 16+ years of global packaging industry experience, we have networks that extend internationally. If you have a sales leadership role in another market, we are happy to discuss whether we can assist.",
  },
  {
    question: "How do you find candidates who aren't actively looking for jobs?",
    answer: "Through targeted headhunting and direct outreach. We identify high-performing sales professionals working within your specific packaging segment and competitor organizations, then approach them confidentially. Our established reputation within the packaging industry means these professionals are willing to have a conversation with us, even when they are not actively job searching.",
  },
  {
    question: "What information do you need to start a search?",
    answer: "We begin with a detailed briefing covering the role requirements, your company culture, the specific packaging segment, compensation expectations, and the commercial challenges the hire needs to address. The more context we have, the more precisely we can target the right candidates.",
  },
];

const COMPANIES_HERO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/companies-hero-CCmjjp9TcwiEgSgW89kLYf.webp";

const differentiators = [
  {
    title: "Industry Knowledge",
    description:
      "We understand the commercial dynamics of packaging — from substrate-specific sales cycles to the competitive landscape across regions.",
    icon: BarChart3,
  },
  {
    title: "Speed Without Compromise",
    description:
      "Our existing network means we can present strong candidates quickly, without sacrificing quality or thoroughness in our assessment.",
    icon: Clock,
  },
  {
    title: "Confidential Process",
    description:
      "Every search is handled with discretion. We protect your brand reputation and ensure candidates are approached professionally.",
    icon: ShieldCheck,
  },
];

export default function ForCompanies() {
  return (
    <div>
      <SEO
        title={pageSEO.forCompanies.title}
        description={pageSEO.forCompanies.description}
        keywords={pageSEO.forCompanies.keywords}
        canonical={pageSEO.forCompanies.canonical}
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [organizationSchema, serviceSchema]
        }}
      />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={COMPANIES_HERO}
            alt="Packaging sales recruitment for companies - OC Recruitment retained search"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2137]/90 via-[#0F2137]/80 to-[#0F2137]/60" />
        </div>
        <div className="container relative z-10 pt-32 pb-20">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "For Companies" },
            ]}
            className="mb-6"
          />
          <div className="max-w-2xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                For Packaging Companies
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h1 className="text-4xl sm:text-5xl text-white leading-[1.1] mb-6">
                Strengthen Your Sales Team With Proven Packaging Talent
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed mb-8">
                OC Recruitment partners with packaging companies on retained
                search assignments to identify and secure the commercial talent
                that drives growth.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-midnight text-sm font-semibold rounded hover:bg-white/90 transition-all group"
              >
                Hire Packaging Sales Talent
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                The Challenge
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h2 className="text-3xl sm:text-4xl text-midnight mb-8">
                Why Hiring Sales Talent in Packaging Is Uniquely Difficult
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="accent-border space-y-4">
                <p className="text-slate-brand leading-relaxed">
                  The packaging industry demands salespeople who understand
                  complex manufacturing processes, long sales cycles, and
                  technical product specifications. A strong sales professional
                  in packaging is not simply someone who can sell — they need
                  deep substrate knowledge, established customer relationships,
                  and the ability to navigate multi-stakeholder procurement
                  decisions.
                </p>
                <p className="text-slate-brand leading-relaxed">
                  These professionals are in high demand and rarely actively
                  looking for new roles. Generalist recruiters struggle to
                  identify them, let alone engage them in a meaningful
                  conversation about their career. This is where a specialist
                  retained search firm makes the difference.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Retained Search — How We Work */}
      <section className="py-20 lg:py-28 bg-midnight text-white">
        <div className="container">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl text-white mb-4">
              Retained Search for Packaging Sales
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <p className="text-lg text-white/60 leading-relaxed mb-12 max-w-3xl">
              Every engagement is a retained search. We commit fully to each
              assignment, combining targeted headhunting, detailed market
              mapping, and direct outreach to deliver a shortlist of proven
              sales professionals from within the packaging industry.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8">
            <ScrollReveal delay={0.1}>
              <div className="group p-8 rounded border border-white/10 hover:border-steel/30 transition-all duration-300">
                <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center mb-6 group-hover:bg-steel/20 transition-colors">
                  <Search size={22} className="text-steel" />
                </div>
                <h3 className="text-xl text-white mb-3 font-['DM_Serif_Display',Georgia,serif]">
                  Targeted Headhunting
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Direct, confidential outreach to high-performing sales
                  professionals working within your target packaging segments
                  and competitor organizations.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="group p-8 rounded border border-white/10 hover:border-steel/30 transition-all duration-300">
                <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center mb-6 group-hover:bg-steel/20 transition-colors">
                  <Target size={22} className="text-steel" />
                </div>
                <h3 className="text-xl text-white mb-3 font-['DM_Serif_Display',Georgia,serif]">
                  Market Mapping
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Comprehensive mapping of the talent landscape within your
                  specific packaging segment, identifying who the top performers
                  are and where they sit.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <div className="group p-8 rounded border border-white/10 hover:border-steel/30 transition-all duration-300">
                <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center mb-6 group-hover:bg-steel/20 transition-colors">
                  <Users size={22} className="text-steel" />
                </div>
                <h3 className="text-xl text-white mb-3 font-['DM_Serif_Display',Georgia,serif]">
                  Candidate Assessment
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Thorough evaluation of each candidate's commercial track
                  record, cultural fit, and long-term potential before
                  presenting them to you.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <ScrollReveal>
                <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                  The OC Recruitment Difference
                </p>
              </ScrollReveal>
              <ScrollReveal delay={0.1}>
                <h2 className="text-3xl sm:text-4xl text-midnight mb-6">
                  A Search Partner Who Understands Packaging
                </h2>
              </ScrollReveal>
              <ScrollReveal delay={0.2}>
                <p className="text-slate-brand leading-relaxed mb-4">
                  When you work with OC Recruitment, you are working with
                  someone who understands the difference between flexible film
                  and rigid plastics, who knows the key players across{" "}
                  <Link href="/packaging-sectors" className="text-steel hover:underline">all eight packaging sectors</Link>,
                  and who can assess whether a candidate
                  truly has the commercial acumen to drive growth in your
                  specific market segment.
                </p>
                <p className="text-slate-brand leading-relaxed">
                  Our specialist focus means we can identify whether a candidate's experience in{" "}
                  <Link href="/packaging-sectors" className="text-steel hover:underline">flexible packaging</Link>{" "}
                  translates to your rigid plastics business, or whether a Sales Director from corrugated
                  has the right skills to lead a labels and sleeves commercial team. This depth of
                  knowledge is what separates a specialist packaging recruiter from a generalist firm.
                  View our{" "}
                  <Link href="/case-studies" className="text-steel hover:underline">case studies</Link>{" "}
                  to see how this approach delivers results.
                </p>
              </ScrollReveal>
            </div>

            <div className="space-y-6">
              {differentiators.map((diff, i) => (
                <ScrollReveal key={diff.title} delay={i * 0.1}>
                  <div className="flex gap-5 p-6 bg-pale rounded border border-transparent hover:border-border transition-colors">
                    <div className="w-10 h-10 rounded bg-midnight/5 flex items-center justify-center flex-shrink-0">
                      <diff.icon size={18} className="text-steel" />
                    </div>
                    <div>
                      <h3 className="text-midnight font-semibold mb-1 font-['DM_Sans',sans-serif]">
                        {diff.title}
                      </h3>
                      <p className="text-slate-brand text-sm leading-relaxed">
                        {diff.description}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ
        title="Frequently Asked Questions"
        subtitle="For Packaging Companies"
        items={companiesFAQs}
        bgClass="bg-pale"
      />

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl text-midnight mb-6">
                Discuss Your Hiring Needs
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-lg text-slate-brand leading-relaxed mb-8">
                Whether you have an immediate vacancy or want to discuss your
                longer-term sales hiring strategy, Chris is available for a
                confidential conversation.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-midnight text-white text-sm font-semibold rounded hover:bg-midnight/90 transition-all group"
              >
                Hire Packaging Sales Talent
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
